import { useMemo, useState } from "react";
import { formatNotificationLocaleDateTime } from "../../helpers";
import "./NotificationErrors.scss";

/**
 * Expandable list of notification delivery/processing errors (newest first).
 * Shown when details payload includes a non-empty `errors` array.
 *
 * @param {object} props
 * @param {unknown} props.errors `GET /notifications/details` — `{ error: string, timestamp: string }[]`.
 * @returns {import('react').JSX.Element}
 */
const NotificationErrors = ({ errors }) => {
    const [expanded, setExpanded] = useState(false);

    const sorted = useMemo(() => {
        const list = [...errors];

        list.sort((a, b) => {
            const ta = new Date(
                a.timestamp != null ? String(a.timestamp) : 0,
            ).getTime();
            const tb = new Date(
                b.timestamp != null ? String(b.timestamp) : 0,
            ).getTime();

            return tb - ta;
        });

        return list;
    }, [errors]);

    const panelId = "notification-details-errors-panel";

    return (
        <div
            className={`notification-details-sheet__errors-wrap${
                expanded
                    ? " notification-details-sheet__errors-wrap--expanded"
                    : ""
            }`}
        >
            <button
                type="button"
                className="notification-details-sheet__errors-toggle"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => setExpanded((v) => !v)}
            >
                <span
                    className="notification-details-sheet__errors-chevron"
                    aria-hidden
                >
                    ▶
                </span>
                <strong className="notification-details-sheet__errors-heading">
                    Errors
                </strong>
            </button>
            <div
                id={panelId}
                className="notification-details-sheet__errors-expand"
                aria-hidden={!expanded}
            >
                <div className="notification-details-sheet__errors-expand-inner">
                    <div
                        role="region"
                        aria-label="Notification errors"
                        className="notification-details-sheet__errors-panel"
                    >
                        <ul
                            className="notification-details-sheet__errors-list"
                            role="list"
                        >
                            {sorted.map((item, index) => (
                                <li
                                    key={`${String(item?.timestamp)}-${index}`}
                                    className="notification-details-sheet__errors-item"
                                    role="listitem"
                                >
                                    <time
                                        className="notification-details-sheet__errors-time"
                                        dateTime={item?.timestamp}
                                    >
                                        {formatNotificationLocaleDateTime(
                                            item?.timestamp,
                                        )}
                                    </time>
                                    <p className="notification-details-sheet__errors-message">
                                        {item?.error}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationErrors;
